import { format, isValid, parseISO } from 'date-fns'

import { formatFullName } from '../applications/utils'
import type { MembershipRenewalRow } from './types'
import { ApplicationResponseDTOStatus, type ApplicationResponseDTO } from '@/utils/api/membership-service/models'

const DATE_FORMAT = 'PPP'

export function parseRenewalDate(value: string | null): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  if (isValid(parsed)) {
    return parsed
  }
  const fallback = new Date(value)
  return Number.isNaN(fallback.getTime()) ? null : fallback
}

export function formatRenewalDate(value: string | null): string {
  const parsed = parseRenewalDate(value)
  if (!parsed) return '—'
  try {
    return format(parsed, DATE_FORMAT)
  } catch (_error) {
    return '—'
  }
}

export function createRenewalRow(renewal: ApplicationResponseDTO): MembershipRenewalRow {
  const duration = computeDurationLabel(renewal?.membershipStartAt ?? null, renewal?.membershipExpiryAt ?? null)
  const remainingDuration = computeRemainingDurationLabel(renewal?.membershipExpiryAt ?? null)
  return {
    id: renewal.id as number,
    name: formatFullName(
      renewal.firstName ?? '',
      renewal.middleName,
      renewal.lastName ?? '',
    ) || 'Unnamed applicant',
    amount: '—',
    status: renewal.status ?? ApplicationResponseDTOStatus.PENDING,
    phone: renewal.phoneNumbers || '—',
    category: renewal.categoryName || 'Uncategorised',
    duration,
    remainingDuration,
    startDateDisplay: formatRenewalDate(renewal?.membershipStartAt ?? null),
    endDateDisplay: formatRenewalDate(renewal?.membershipExpiryAt ?? null),
    proofOfPaymentUrl: renewal.documents?.find((doc => doc.documentType.toLocaleUpperCase() === 'PROOF OF PAYMENT'))?.fileUrl ?? undefined,
  }
}

function computeDurationLabel(startDate: string | null, endDate: string | null): string {
  const start = parseRenewalDate(startDate)
  const end = parseRenewalDate(endDate)
  if (!start || !end) return '—'

  const diffMs = end.getTime() - start.getTime()
  if (diffMs <= 0) return '—'

  const totalDays = Math.round(diffMs / (1000 * 60 * 60 * 24)) + 1
  const months = Math.floor(totalDays / 30)
  const years = Math.floor(months / 12)
  if (years >= 1) {
    return `${years} year${years === 1 ? '' : 's'}`
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'}`
  }

  return `${totalDays} day${totalDays === 1 ? '' : 's'}`
}

function computeRemainingDurationLabel(endDate: string | null): string | null {
  const end = parseRenewalDate(endDate)
  if (!end) return null

  const now = new Date()
  const diffMs = end.getTime() - now.getTime()
  if (diffMs <= 0) return '0 days'

  const totalDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24))
  const months = Math.floor(totalDays / 30)
  const years = Math.floor(months / 12)

  if (years >= 1) {
    return `${years} year${years === 1 ? '' : 's'}`
  }
  if (months >= 1) {
    return `${months} month${months === 1 ? '' : 's'}`
  }

  return `${totalDays} day${totalDays === 1 ? '' : 's'}`
}


export function formatDateForInput(value: string | null): string {
  const parsed = parseRenewalDate(value)
  if (!parsed) return ''
  try {
    return format(parsed, 'yyyy-MM-dd')
  } catch (_error) {
    return ''
  }
}